/**
 * Post a notice: severity, title, text, the window (duration OR end), target apps from the
 * registry — and the band preview next to it (plan §7 M2).
 *
 * Every notice ends (§9): there is no "until further notice" option, and the API caps the window
 * at 30 days. The times typed here are the browser's local time; they are sent as UTC
 * (`toISOString()`, correct here — it is an instant, not a calendar date) and shown back in
 * Swiss time.
 */
import { useMemo, useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { NoticeBandPreview } from './NoticeBandPreview';
import { formatDateTimeCH } from '../../utils/format';
import {
    NOTICE_MAX_BODY, NOTICE_MAX_TITLE, NOTICE_MAX_WINDOW_MINUTES,
    type NewNoticeRequest, type NoticeApp, type NoticeSeverity, type NoticeTargetPage,
} from '../../types/notices';

/** The registry key a page target is limited to — mirrors PAGE_TARGET_APP in the API. */
const PAGE_TARGET_APP = 'reporting';

const SEVERITY_OPTIONS: { value: NoticeSeverity; label: string; hint: string; active: string }[] = [
    { value: 'info', label: 'Info', hint: 'For your information', active: 'border-blue-600 bg-blue-50 text-blue-900' },
    { value: 'warning', label: 'Warning', hint: 'You have to account for this', active: 'border-amber-500 bg-amber-50 text-amber-900' },
    { value: 'critical', label: 'Critical', hint: 'Stop — something is down', active: 'border-red-600 bg-red-50 text-red-900' },
];

type Unit = 'minutes' | 'hours' | 'days';
const UNIT_MINUTES: Record<Unit, number> = { minutes: 1, hours: 60, days: 1440 };
const PRESETS: { label: string; value: number; unit: Unit }[] = [
    { label: '30 min', value: 30, unit: 'minutes' },
    { label: '1 h', value: 1, unit: 'hours' },
    { label: '2 h', value: 2, unit: 'hours' },
    { label: '4 h', value: 4, unit: 'hours' },
    { label: '1 day', value: 1, unit: 'days' },
    { label: '3 days', value: 3, unit: 'days' },
];

/** `datetime-local` value (local wall time) → Date, or null. */
function parseLocal(value: string): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** Date → `datetime-local` value in the browser's zone (no toISOString: that is UTC). */
function toLocalInput(d: Date): string {
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The window as it will be stored, or why it cannot be. */
type NoticeWindow = { ok: true; from: Date; until: Date } | { ok: false; error: string };

interface Props {
    apps: NoticeApp[];
    /** Active pages of this app, for the optional page target (migration 216). */
    pages: NoticeTargetPage[];
    submitting: boolean;
    onSubmit: (req: NewNoticeRequest) => Promise<boolean>;
}

export function NoticeForm({ apps, pages, submitting, onSubmit }: Props) {
    const [severity, setSeverity] = useState<NoticeSeverity>('info');
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [startMode, setStartMode] = useState<'now' | 'at'>('now');
    const [startAt, setStartAt] = useState('');
    const [endMode, setEndMode] = useState<'duration' | 'end'>('duration');
    const [durationValue, setDurationValue] = useState(2);
    const [durationUnit, setDurationUnit] = useState<Unit>('hours');
    const [endAt, setEndAt] = useState('');
    const [selectedApps, setSelectedApps] = useState<string[]>([]);
    const [targetPage, setTargetPage] = useState('');
    const [touched, setTouched] = useState(false);

    // A page only refines the Reporting App, so it is offered only when that is the ONE target.
    // Any other choice of apps sends no page — the API would refuse one.
    const pageAllowed = selectedApps.length === 1 && selectedApps[0] === PAGE_TARGET_APP;

    const minutes = Math.round(durationValue * UNIT_MINUTES[durationUnit]);

    /** The window as it will be stored, or the reason it cannot be. Recomputed per render. */
    const win = useMemo((): NoticeWindow => {
        const now = new Date();
        const from = startMode === 'now' ? now : parseLocal(startAt);
        if (!from) return { ok: false, error: 'Pick a start time.' };
        if (startMode === 'at' && from.getTime() < now.getTime() - 5 * 60_000) return { ok: false, error: 'The start lies in the past.' };
        let until: Date | null;
        if (endMode === 'duration') {
            if (!Number.isFinite(minutes) || minutes < 1) return { ok: false, error: 'The duration must be at least one minute.' };
            if (minutes > NOTICE_MAX_WINDOW_MINUTES) return { ok: false, error: 'A notice may run for at most 30 days.' };
            until = new Date(from.getTime() + minutes * 60_000);
        } else {
            until = parseLocal(endAt);
            if (!until) return { ok: false, error: 'Pick an end time.' };
            if (until.getTime() <= from.getTime()) return { ok: false, error: 'The end must be after the start.' };
            if (until.getTime() <= now.getTime()) return { ok: false, error: 'The end lies in the past.' };
            if (until.getTime() - from.getTime() > NOTICE_MAX_WINDOW_MINUTES * 60_000) return { ok: false, error: 'A notice may run for at most 30 days.' };
        }
        return { ok: true, from, until };
        // startMode 'now' reads the clock; re-evaluated on every keystroke, which is enough.
    }, [startMode, startAt, endMode, minutes, endAt]);

    const problems: string[] = [];
    if (!title.trim()) problems.push('Title is required.');
    if (!body.trim()) problems.push('Text is required.');
    if (!win.ok) problems.push(win.error);
    if (selectedApps.length === 0) problems.push('Pick at least one target app.');

    const toggleApp = (key: string) =>
        setSelectedApps(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

    const reset = () => {
        setSeverity('info'); setTitle(''); setBody(''); setStartMode('now'); setStartAt(''); setEndMode('duration');
        setDurationValue(2); setDurationUnit('hours'); setEndAt(''); setSelectedApps([]); setTargetPage(''); setTouched(false);
    };

    const submit = async () => {
        setTouched(true);
        if (problems.length > 0 || !win.ok) return;
        const req: NewNoticeRequest = {
            severity,
            title: title.trim(),
            body: body.trim(),
            apps: selectedApps,
            ...(pageAllowed && targetPage ? { page: targetPage } : {}),
            ...(startMode === 'at' ? { validFrom: win.from.toISOString() } : {}),
            ...(endMode === 'duration' ? { minutes } : { validUntil: win.until.toISOString() }),
        };
        if (await onSubmit(req)) reset();
    };

    const radio = (active: boolean) =>
        `rounded border px-2.5 py-1 text-xs ${active ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`;

    return (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2" data-testid="notice-form">
            <div className="space-y-4">
                <fieldset>
                    <legend className="text-xs font-medium text-gray-600">Severity</legend>
                    <div className="mt-1 flex flex-wrap gap-2">
                        {SEVERITY_OPTIONS.map(o => (
                            <button
                                key={o.value}
                                type="button"
                                onClick={() => setSeverity(o.value)}
                                title={o.hint}
                                data-testid={`severity-${o.value}`}
                                className={`rounded-md border px-3 py-1.5 text-sm ${severity === o.value ? o.active : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50'}`}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </fieldset>

                <label className="block text-xs font-medium text-gray-600">
                    <span className="flex justify-between">
                        <span>Title</span>
                        <span className="font-normal text-gray-400">{title.trim().length} / {NOTICE_MAX_TITLE}</span>
                    </span>
                    <input
                        type="text"
                        value={title}
                        maxLength={NOTICE_MAX_TITLE}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="e.g. Business Central maintenance tonight"
                        data-testid="notice-title"
                        className="mt-1 w-full rounded border px-2 py-1.5 text-sm font-normal text-gray-900"
                    />
                </label>

                <label className="block text-xs font-medium text-gray-600">
                    <span className="flex justify-between">
                        <span>Text</span>
                        <span className="font-normal text-gray-400">{body.trim().length} / {NOTICE_MAX_BODY}</span>
                    </span>
                    <textarea
                        value={body}
                        maxLength={NOTICE_MAX_BODY}
                        rows={3}
                        onChange={e => setBody(e.target.value)}
                        placeholder="What readers need to know and do. One to three sentences."
                        data-testid="notice-body"
                        className="mt-1 w-full rounded border px-2 py-1.5 text-sm font-normal text-gray-900"
                    />
                </label>

                <fieldset className="space-y-2">
                    <legend className="text-xs font-medium text-gray-600">Window</legend>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <span className="w-10">Start</span>
                        <button type="button" className={radio(startMode === 'now')} onClick={() => setStartMode('now')}>Now</button>
                        <button type="button" className={radio(startMode === 'at')} onClick={() => { setStartMode('at'); if (!startAt) setStartAt(toLocalInput(new Date(Date.now() + 3600_000))); }}>At</button>
                        {startMode === 'at' && (
                            <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className="rounded border px-2 py-1 text-sm" data-testid="notice-start" />
                        )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                        <span className="w-10">End</span>
                        <button type="button" className={radio(endMode === 'duration')} onClick={() => setEndMode('duration')}>Duration</button>
                        <button type="button" className={radio(endMode === 'end')} onClick={() => { setEndMode('end'); if (!endAt) setEndAt(toLocalInput(new Date(Date.now() + 2 * 3600_000))); }}>End at</button>
                        {endMode === 'duration' ? (
                            <>
                                <input
                                    type="number" min={1} step={1} value={durationValue}
                                    onChange={e => setDurationValue(Number(e.target.value))}
                                    className="w-20 rounded border px-2 py-1 text-sm" data-testid="notice-duration"
                                />
                                <select value={durationUnit} onChange={e => setDurationUnit(e.target.value as Unit)} className="rounded border px-2 py-1 text-sm" data-testid="notice-duration-unit">
                                    <option value="minutes">minutes</option>
                                    <option value="hours">hours</option>
                                    <option value="days">days</option>
                                </select>
                            </>
                        ) : (
                            <input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className="rounded border px-2 py-1 text-sm" data-testid="notice-end" />
                        )}
                    </div>
                    {endMode === 'duration' && (
                        <div className="flex flex-wrap gap-1.5 pl-12">
                            {PRESETS.map(p => (
                                <button
                                    key={p.label}
                                    type="button"
                                    onClick={() => { setDurationValue(p.value); setDurationUnit(p.unit); }}
                                    className="rounded-full border border-gray-300 px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>
                    )}
                    <p className="pl-12 text-xs text-gray-500" data-testid="notice-window-summary">
                        {win.ok
                            ? <>Shown from <b>{startMode === 'now' ? 'now' : formatDateTimeCH(win.from.toISOString())}</b> until <b>{formatDateTimeCH(win.until.toISOString())}</b> (Swiss time). Every notice ends — at most 30 days.</>
                            : <span className="text-amber-700">{win.error}</span>}
                    </p>
                </fieldset>

                <fieldset>
                    <legend className="text-xs font-medium text-gray-600">Target apps</legend>
                    <div className="mt-1 flex flex-wrap gap-3">
                        {apps.map(a => (
                            <label key={a.appKey} className="inline-flex items-center gap-1.5 text-sm text-gray-800">
                                <input
                                    type="checkbox"
                                    checked={selectedApps.includes(a.appKey)}
                                    onChange={() => toggleApp(a.appKey)}
                                    data-testid={`notice-app-${a.appKey}`}
                                />
                                {a.appName}
                            </label>
                        ))}
                        {apps.length === 0 && <span className="text-xs text-gray-500">No active app in the registry.</span>}
                    </div>
                </fieldset>

                {pageAllowed && pages.length > 0 && (
                    <label className="block text-xs font-medium text-gray-600">
                        Page <span className="font-normal text-gray-400">(optional)</span>
                        <select
                            value={targetPage}
                            onChange={e => setTargetPage(e.target.value)}
                            data-testid="notice-page"
                            className="mt-1 w-full rounded border px-2 py-1.5 text-sm font-normal text-gray-900"
                        >
                            <option value="">The whole Reporting App</option>
                            {pages.map(p => (
                                <option key={p.pageUrl} value={p.pageUrl}>{p.pageName} ({p.pageUrl})</option>
                            ))}
                        </select>
                        <span className="mt-1 block font-normal text-gray-500">
                            Shown only on that page, and only to people who can open it.
                        </span>
                    </label>
                )}

                {touched && problems.length > 0 && (
                    <ul className="list-disc rounded border border-amber-200 bg-amber-50 py-2 pl-6 pr-3 text-xs text-amber-800" data-testid="notice-problems">
                        {problems.map(p => <li key={p}>{p}</li>)}
                    </ul>
                )}

                <button
                    type="button"
                    onClick={submit}
                    disabled={submitting}
                    data-testid="notice-submit"
                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Post notice
                </button>
            </div>

            <div className="space-y-2">
                <p className="text-xs font-medium text-gray-600">Preview — how the band appears at the top of each target app</p>
                <NoticeBandPreview severity={severity} title={title} body={body} />
                <p className="text-xs text-gray-500">
                    Readers can close the band; the notice then stays readable in the app's What's New panel until it ends.
                    It does not light the bell dot. Connected apps pick it up without a deploy.
                </p>
            </div>
        </div>
    );
}
